import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CATEGORIES = ["All", "General", "Earnings", "Security", "Payouts"];

const FAQS = [
  {
    category: "General",
    q: "What exactly is Honeygain?",
    a: "Honeygain is an app that lets you earn money by sharing your unused internet bandwidth. Companies use that bandwidth for things like price comparison, ad verification and content delivery — and you get paid for it.",
  },
  {
    category: "General",
    q: "Does it cost anything to join?",
    a: "No, joining is completely free. Even better — if you sign up through the link on this page, a $3 bonus is added straight to your balance.",
  },
  {
    category: "Earnings",
    q: "How much can I realistically earn?",
    a: "Most users earn between $20 and $60 per month depending on location, network demand, bandwidth shared and the number of devices. More devices and faster connections generally mean higher earnings.",
  },
  {
    category: "Earnings",
    q: "Will it slow down my internet?",
    a: "For most users the impact is barely noticeable. You can also set limits on how much bandwidth the app uses, or pause it entirely whenever you want.",
  },
  {
    category: "Security",
    q: "Is my personal data safe?",
    a: "Yes. Honeygain never accesses your files, browsing history or personal data. All traffic is encrypted, and the app only shares bandwidth — not information about you.",
  },
  {
    category: "Security",
    q: "Who uses my bandwidth?",
    a: "Only vetted business partners, for legitimate purposes such as web research, SEO monitoring and content delivery. Every partner goes through a strict verification process.",
  },
  {
    category: "Security",
    q: "Will this affect my ISP standing or my home IP?",
    a: "Honeygain routes encrypted business traffic through your connection, which is legal in most regions. Only bandwidth is shared — never your files, browsing activity or identity. If your plan has a strict data cap or your ISP's terms restrict connection sharing, check those terms first; you can set usage limits or pause sharing anytime.",
  },
  {
    category: "Payouts",
    q: "How do payouts work?",
    a: "You earn credits for every GB shared. Once you reach the $20 minimum, you can cash out via PayPal or in crypto (JumpTask). The $3 sign-up bonus from this link counts toward that threshold.",
  },
  {
    category: "Payouts",
    q: "How fast do I get my money?",
    a: "PayPal payouts are typically processed within a few business days. Crypto payouts via JumpTask are usually much faster — often near-instant.",
  },
];

const FAQAccordion = () => {
  const [category, setCategory] = useState("All");
  const filtered = category === "All" ? FAQS : FAQS.filter((f) => f.category === category);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-700">Questions</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900">
            Everything you're wondering, answered
          </h2>
        </Reveal>

        <Reveal delay={0.1} className="mt-9 flex flex-wrap justify-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`faq-filter-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                category === c
                  ? "bg-zinc-900 text-white"
                  : "bg-amber-50 text-zinc-700 border border-amber-200 hover:bg-amber-100"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <Accordion type="single" collapsible className="space-y-3">
            {filtered.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                data-testid="faq-item"
                className="rounded-2xl border border-zinc-100 bg-[#FAF8F5] px-6 data-[state=open]:border-amber-300 data-[state=open]:bg-amber-50/60 transition-colors"
              >
                <AccordionTrigger className="text-left font-display font-bold text-zinc-900 hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export default FAQAccordion;
